import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import PropTypes from 'prop-types';

SubscriptionPage.propTypes = {
  openForm: PropTypes.func,
  openPurchaseForm: PropTypes.func,
};

export default function SubscriptionPage(props) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const token = localStorage.getItem('token');
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const sortedPlans = plans.slice().sort((a, b) => a.subscriptionAmount - b.subscriptionAmount);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const freeTrialPlan = sortedPlans.find((plan) => plan.subscriptionTitle === 'Free');
  const basicPlan = sortedPlans.find((plan) => plan.subscriptionTitle === 'Basic');
  const standardPlan = sortedPlans.find((plan) => plan.subscriptionTitle === 'Standard');
  const contactSalesPlan = sortedPlans.find((plan) => plan.subscriptionTitle === 'Enterprise');
  const plansToRender = [freeTrialPlan, basicPlan, standardPlan, contactSalesPlan];
  const [runningPlan, setRunningPlan] = useState(null);

  useEffect(() => {
    async function fetchPlans() {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/getAllMasterData`);
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        if (data.success) setPlans(data.data);
        else console.error('Failed to fetch data');
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    async function fetchRunningPlan() {
      try {
        const cleanToken = token.replace(/"/g, '');
        const config = {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
        };

        const response = await axios.get(`${API_BASE_URL}/userPlanOverview`, config);

        if (response.data && response.data.data) {
          const plans = Object.entries(response.data.data).map(([id, plan]) => ({
            id,
            ...plan,
          }));
          const activePlan = plans.length > 0 ? plans[0] : null;
          setRunningPlan(activePlan.subscriptionPlanId || null);
        } else {
          setRunningPlan(null);
        }
      } catch (error) {
        console.error('Error fetching running plan:', error);
      }
    }

    fetchPlans();
    fetchRunningPlan();
  }, []);

  const handleSelectPlan = (plan) => {
    if (plan.subscriptionTitle === 'Enterprise') {
      props.openForm(plan._id);
      navigate(`/dashboard/subscription/${plan._id}`);
    } else {
      props.openPurchaseForm(plan._id);
      navigate(
        `/dashboard/subscription/${encodeURIComponent(plan._id)}/${encodeURIComponent(plan.subscriptionTitle)}/${encodeURIComponent(plan.subscriptionAmount)}`
      );
    }
  };

  return isLoading ? (
    <CircularProgress style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }} />
  ) : (
    <div id="pricing" style={styles.container}>
      <h2 style={styles.heading}>Pricing Plan</h2>
      <Typography variant="h6" color="gray" mb={6}>
        Choose the plan that suits your needs
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: { xs: 'wrap', md: 'nowrap' }, // xs < 940px, md >= 940px
        }}
      >
        {plansToRender.map((plan, idx) => {
          if (!plan) return null;
          const isHovered = hoveredIndex === idx;
          const isContactSales = plan.subscriptionTitle === 'Enterprise';
          const isPopular = plan.subscriptionTitle === 'Standard';

          const isDisable = (runningPlan === plan._id);
          return (
            <div
              key={idx}
              role="button"
              tabIndex={isDisable ? -1 : 0}
              onClick={!isDisable ? () => handleSelectPlan(plan) : undefined}
              onKeyDown={
                !isDisable
                  ? (e) => ['Enter', ' '].includes(e.key) && handleSelectPlan(plan)
                  : undefined
              }
              style={{
                ...styles.planCard,
                ...(isHovered || isPopular ? styles.featuredCard : {}),
                ...(isPopular ? styles.popularCard : {}),
                ...(isDisable
                  ? {
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                    opacity: 0.6,
                  }
                  : {}),
              }}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {isPopular && <div style={styles.popularBadge}>⭐ Popular</div>}

              <div style={styles.cardContent}>
                <h3 style={styles.planName}>{plan.subscriptionTitle}</h3>
                <p style={styles.planPrice}>
                  {isContactSales ? (
                    'Contact Sales'
                  ) : (plan.subscriptionTitle === 'Standard' || plan.subscriptionTitle === 'Basic') ? (
                    <>
                      ${plan.subscriptionAmount}/month<sup style={{ fontSize: '0.5em' }}>#</sup>
                    </>
                  ) : (
                    `$${plan.subscriptionAmount}/month`
                  )}
                </p>

                <Typography variant="subtitle2" align="center" sx={{ my: 1, px: 1, minHeight: '40px' }}>
                  {plan.subscriptionDescription}
                </Typography>

                <ul style={styles.featuresList}>
                  {plan.features.map((feature, index) => {
                    const lowerFeature = feature.trim().toLowerCase();
                    const isNegative = lowerFeature.startsWith('monitoring') || lowerFeature.startsWith('assisted');

                    return (
                      <li key={index} style={styles.featureItem}>
                        <span
                          style={{
                            ...styles.iconMark,
                            backgroundColor: isNegative ? '#a1a1a19e' : '#007bff',
                          }}
                        >
                          {isNegative ? '✖' : '✔'}
                        </span>
                        <span style={styles.featureText}>{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div
                style={{
                  ...styles.button,
                  ...(isHovered || isPopular ? styles.featuredButton : {}),
                  ...(runningPlan === plan._id ? styles.disabledButton : {}),
                }}
              >
                {runningPlan === plan._id ? "Activated" : plan.buttonText}
              </div>
            </div>
          );
        })}
      </Box>

      {/* Additional Notes */}
      <Box sx={styles.notesContainer}>
        <Box sx={styles.noteItem}>
          <span style={styles.noteIcon}>*</span>
          <Typography variant="body1" sx={styles.noteText}>
            We have in-house NOC team available 24x7 to help you manage escalation and deal with false alarms.
          </Typography>
        </Box>

        <Box sx={styles.noteItem}>
          <span style={styles.noteIcon}>**</span>
          <Typography variant="body1" sx={styles.noteText}>
            Database, webserver and other popular metrics available.
          </Typography>
        </Box>

        <Box sx={styles.noteItem}>
          <Typography
            variant="caption"
            sx={{ ...styles.noteText, color: 'gray', display: 'flex', alignItems: 'center' }}
          >
            <span style={{ fontWeight: 'bold', paddingRight: '4px' }}>#</span>
            Prices do not include tax. Additional charges may be applied at checkout.
          </Typography>
        </Box>
      </Box>

    </div >
  );
}

const getStyles = (theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    container: {
      textAlign: 'center',
      paddingBottom: '30px',
    },

    heading: {
      fontSize: '2.4rem',
      marginBottom: '40px',
      fontWeight: '700',
      color: isDark ? theme.palette.text.primary : '#002244',
    },

    planCard: {
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderRadius: '14px',
      boxShadow: isDark
        ? '0 6px 18px rgba(0,0,0,0.6)'
        : '0 6px 18px rgba(0,0,0,0.1)',
      width: '300px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: '20px',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'pointer',
      minHeight: '420px',
    },

    featuredCard: {
      transform: 'scale(1.05)',
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: `0 10px 28px ${theme.palette.primary.main}55`,
    },
    cardContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      paddingBottom: '16px',
    },
    planName: {
      fontSize: '1.3rem',
      fontWeight: '700',
      marginBottom: '4px',
    },
    planPrice: {
      fontSize: '1.5rem',
      fontWeight: '800',
      color: theme.palette.primary.main,
      marginBottom: '4px',
      marginTop: '4px',
    },
    featuresList: {
      listStyleType: 'none',
      padding: 0,
      marginTop: '1px',
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      maxHeight: '240px',
      overflowY: 'auto',
      scrollbarWidth: 'thin',
    },
    featureItem: {
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
      display: 'flex',
      alignItems: 'center',
    },

    iconMark: {
      display: 'inline-block',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      width: '18px',
      height: '18px',
      textAlign: 'center',
      lineHeight: '18px',
      marginRight: '8px',
      fontWeight: '700',
      fontSize: '0.75rem',
    },

    button: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '0.85rem',
      fontWeight: '600',
      cursor: 'pointer',
      letterSpacing: '0.04em',
      boxShadow: `0 5px 10px ${theme.palette.primary.main}55`,
      transition: 'all 0.3s ease',
      marginTop: 'auto',
      marginBottom: '0',
      textAlign: 'center',
    },

    disabledButton: {
      opacity: 0.6,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      boxShadow: 'none',
    },
    featuredButton: {
      backgroundColor: '#004080',
      boxShadow: '0 7px 16px rgba(0, 64, 128, 0.35)',
    },
    featureText: {
      flex: 1,
      wordBreak: 'break-word',
    },
    popularCard: {
      position: 'relative',
      borderColor: '#2065D1',
      boxShadow: '0 10px 28px rgba(52, 159, 236, 0.3)',
    },
    popularBadge: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      padding: '3px 8px',
      borderRadius: '18px',
      fontSize: '0.7rem',
      fontWeight: 'bold',
       zIndex: 1,
    },
    notesContainer: {
      mt: 8,
      textAlign: 'center',
      maxWidth: '900px',
      mx: 'auto',
      px: 2,
    },
    noteItem: {
      display: 'flex',
      alignItems: 'flex-start',
      textAlign: 'left',
      mb: 1.5,
    },
    noteIcon: {
      fontSize: '1.2rem',
      flexShrink: 0,
      marginTop: 1,
      fontWeight: 'bold',
      paddingRight: '4px'
    },
    noteText: {
      fontSize: '1.1rem',
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
       textAlign: 'left',
    },
  };
};
